/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { HelpCircle, X } from 'lucide-react';
import { getMediaCategory } from '../../constants/mimeMap';
import CachedImage from '../CachedImage';
import PdfInstructionViewer from '../PdfInstructionViewer';

// Bottom-of-screen "?" trigger for the workflow-level global instruction
// (as opposed to the per-step reference media). Renders nothing when no
// globalInstructionUrl is configured — same visibility rule CaptureHeader
// used to enforce before this icon moved out of the header. token is
// forwarded to the cached-asset fallbacks below so this stays viewable
// offline once assetCache has prefetched globalInstructionUrl.
const GlobalInstructionButton = ({ globalInstructionUrl, token, className = '' }) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsLoading, setInstructionsLoading] = useState(false);

  useEffect(() => {
    if (!showInstructions) return;
    setInstructionsLoading(true);
    const timeout = setTimeout(() => setInstructionsLoading(false), 4000);
    return () => clearTimeout(timeout);
  }, [showInstructions]);

  if (!globalInstructionUrl) return null;

  const instructionCategory = getMediaCategory(null, globalInstructionUrl);

  return (
    <>
      <button
        type="button"
        onClick={() => setShowInstructions(true)}
        title="View instructions"
        aria-label="View global instructions"
        className={`w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-md border border-primary active:scale-95 transition-transform ${className}`}
      >
        <HelpCircle size={26} className="text-primary" />
      </button>

      {showInstructions && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Global instructions"
          onClick={() => setShowInstructions(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
        >
          <button
            onClick={() => setShowInstructions(false)}
            aria-label="Close instructions"
            style={{ position: 'absolute', top: 20, right: 20, zIndex: 100000, background: 'rgba(255, 255, 255, 0.79)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'black' }}
          >
            <X size={20} />
          </button>
          {instructionCategory === 'image' && (
            <CachedImage
              token={token}
              src={globalInstructionUrl}
              alt="Instructions"
              onClick={e => e.stopPropagation()}
              onLoad={() => setInstructionsLoading(false)}
              onFallbackFailed={() => setInstructionsLoading(false)}
              style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )}
          {instructionCategory === 'video' && (
            <CachedImage
              as="video"
              token={token}
              src={globalInstructionUrl}
              controls
              autoPlay
              playsInline
              onClick={e => e.stopPropagation()}
              onLoadedData={() => setInstructionsLoading(false)}
              onFallbackFailed={() => setInstructionsLoading(false)}
              style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )}
          {instructionCategory === 'document' && (
            <PdfInstructionViewer
              url={globalInstructionUrl}
              token={token}
              onClick={e => e.stopPropagation()}
              onLoad={() => setInstructionsLoading(false)}
              onError={() => setInstructionsLoading(false)}
            />
          )}
          {instructionsLoading && (
            <div
              style={{ position: 'absolute', inset: 0, zIndex: 99998, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
            >
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="w-8 h-8 rounded-full border-4 border-white/20 border-t-white animate-spin" />
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

GlobalInstructionButton.propTypes = {
  globalInstructionUrl: PropTypes.string,
  token: PropTypes.string,
  className: PropTypes.string,
};

export default GlobalInstructionButton;
